import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary-hover active:bg-primary-pressed',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive-hover active:bg-destructive-pressed',
        outline:
          'border text-outline-foreground border-input bg-background shadow-sm hover:text-outline-hover active:bg-accent-pressed',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary-hover active:bg-secondary-pressed',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'underline-offset-4 underline decoration-link-border hover:decoration-primary-foreground',
        disabled: 'bg-muted text-muted-foreground',
        // Alias of the default primary style for explicit primary variant usage
        primary: 'bg-primary text-primary-foreground shadow hover:bg-primary-hover active:bg-primary-pressed',
        // Success variant following the green color palette used in badge utility variants
        success: 'bg-[#009735] text-[#FAF4F0] shadow hover:bg-[#27B85A] active:bg-[#006623]',
      },
      size: {
        default: 'h-9 px-4 py-2 rounded-lg',
        sm: 'h-8 rounded-lg px-3 text-xs',
        md: 'h-10 rounded-lg px-3',
        lg: 'h-10 rounded-lg px-8',
        icon: 'h-9 w-9 rounded-lg',
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, disabled, loading, children, ...props },
    ref
  ) => {
    // When rendering asChild we clone the single valid React element passed as children
    if (asChild && React.isValidElement(children)) {
      const Comp = Slot
      return (
        <Comp
          className={cn(
            buttonVariants({ variant: disabled ? 'disabled' : variant, size }),
            className
          )}
          ref={ref}
          {...props}
        >
          {children}
        </Comp>
      )
    }

    const Comp: React.ElementType = 'button'
    const isDisabled = disabled || loading

    return (
      <Comp
        className={cn(
          buttonVariants({ variant: isDisabled ? 'disabled' : variant, size }),
          className
        )}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading && (
          <Loader2 className="animate-spin h-4 w-4" />
        )}
        {children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
